import { ReactNode, useCallback, useEffect } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Button from '@mui/material/Button';

import { type Table as ReactTableType } from '@tanstack/react-table';
import Box from '@mui/material/Box';
import MainCard from '@components/ui/cards/MainCard';

import { useSearchParams } from 'react-router-dom';
import styles from './index.module.scss';

interface MainTablePaginationProps<TData extends Record<string, any>> {
  table: ReactTableType<TData>;
  isLoading: boolean;
}

function MainTablePaginationComponent<TData extends Record<string, any>>({
  table,
  isLoading = false,
}: MainTablePaginationProps<TData>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = Number(searchParams.get('page') ?? '1') - 1;

  const { pageIndex } = table.getState().pagination;
  const maxPages = table.getPageCount();

  useEffect(() => {
    if (urlPage >= 0 && urlPage < maxPages && urlPage !== pageIndex) {
      table.setPageIndex(urlPage);
    }
  }, [urlPage, maxPages, pageIndex, table]);

  const setCurrentPage = useCallback(
    (newPage: number) => {
      const safe = Math.min(Math.max(newPage, 0), Math.max(maxPages - 1, 0));
      table.setPageIndex(safe);
      const next = new URLSearchParams(searchParams);
      next.set('page', String(safe + 1));
      setSearchParams(next);
    },
    [maxPages, searchParams, setSearchParams, table]
  );

  const createPageButton = useCallback(
    (page: number, active: boolean) => (
      <Box
        key={page}
        className={[styles.pageButton, active ? styles.activeButton : ''].join(
          ' '
        )}
        onClick={() => setCurrentPage(page - 1)}
      >
        <p>{page}</p>
      </Box>
    ),
    [setCurrentPage]
  );

  const renderPageButtons = useCallback(() => {
    if (maxPages <= 1) return null;
    const pages: ReactNode[] = [];
    let start;
    let end;
    const current = pageIndex;

    if (maxPages <= 5 || current < 3) {
      start = 0;
      end = Math.min(5, maxPages) - 1;
    } else if (current > maxPages - 4) {
      start = maxPages - 5;
      end = maxPages - 1;
    } else {
      start = current - 1;
      end = current + 1;
    }

    if (start > 0) {
      pages.push(createPageButton(1, false));
      if (start > 1)
        pages.push(
          <p key="ell-1" className={styles.pageButton}>
            …
          </p>
        );
    }
    for (let i = start; i <= end; i += 1) {
      pages.push(createPageButton(i + 1, i === current));
    }
    if (end < maxPages - 1) {
      if (end < maxPages - 2)
        pages.push(
          <p key="ell-2" className={styles.pageButton}>
            …
          </p>
        );
      pages.push(createPageButton(maxPages, false));
    }

    return pages;
  }, [maxPages, pageIndex, createPageButton]);

  if (isLoading || maxPages <= 1 || table.getRowModel().rows.length === 0) {
    return null;
  }

  return (
    <MainCard backgroundColor="white" borderRadius="10px">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        {table.getCanPreviousPage() ? (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setCurrentPage(pageIndex - 1)}
          >
            Prev
          </Button>
        ) : (
          <div />
        )}
        <Box display="flex">{renderPageButtons()}</Box>
        {table.getCanNextPage() ? (
          <Button
            endIcon={<ArrowForwardIcon />}
            onClick={() => setCurrentPage(pageIndex + 1)}
          >
            Next
          </Button>
        ) : (
          <div />
        )}
      </Box>
    </MainCard>
  );
}

export default MainTablePaginationComponent;
